import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import SingleFreeQuestionView from '../FreeQuestions/SingleFreeQuestionView';
import { trackEvent } from '../../analytics';
import './TrialQuestions.css';

const apiUrl = process.env.REACT_APP_API_URL;

const AWNSERS = ['A', 'B', 'C', 'D', 'E'];

function TrialQuestions() {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan || null;

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showAnswer, setShowAnswer] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    // "plan" é opcional aqui: quando o passo de plano de estudos do funil guiado está
    // desativado (ver GUIDED_FLOW_STUDY_PLAN_STEP_ENABLED em MyMocks.js), o usuário
    // chega direto nessa tela sem ter passado por lá.
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const response = await axios.post(apiUrl + 'freeQuestions/trial', {});
        const items = response.data.questions || [];
        setQuestions(items);
        setShowAnswer(Array(items.length).fill(false));
        setSelectedOptions([]);
        setCurrentQuestionIndex(0);
      } catch {
        alert('Erro ao carregar as questões. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getQuestionImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) return imageUrl;
    return `${apiUrl}uploads/${imageUrl}`;
  };

  const getAnswers = (optionIndex) => `${AWNSERS[optionIndex]})`;

  const handleOptionChange = (questionIndex, option, optionIndex) => {
    setSelectedOptions((prev) => ({ ...prev, [questionIndex]: optionIndex }));
    setShowAnswer((prev) => {
      const updated = [...prev];
      updated[questionIndex] = false;
      return updated;
    });
  };

  const handleAnswer = async (option, questionIndex) => {
    if (option === undefined) {
      alert('Selecione uma alternativa antes de responder.');
      return;
    }

    const updatedShowAnswer = [...showAnswer];
    updatedShowAnswer[questionIndex] = true;
    setShowAnswer(updatedShowAnswer);

    try {
      await axios.post(apiUrl + 'sendFreeAwnser', {
        questionId: questions[questionIndex].id,
        optionSelected: AWNSERS[option]
      });
    } catch {
      alert('Erro ao salvar sua resposta. Tente novamente.');
    }
  };

  const goToResults = () => {
    const answeredQuestions = questions.map((question, index) => ({
      subjectName: question.subjectName,
      isCorrect: AWNSERS[selectedOptions[index]] === question.correct
    }));
    const total = answeredQuestions.length;
    const correct = answeredQuestions.filter((q) => q.isCorrect).length;

    trackEvent('trial_flow_completed', { correct, total });
    // Funil guiado desativado por enquanto — não apagar, só comentado (ver MyMocks.js).
    // localStorage.setItem('trialFlowCompleted', 'true');

    navigate('/questao-treino/resultado', {
      replace: true,
      state: { answeredQuestions, plan }
    });
  };

  const allAnswered = questions.length > 0 && showAnswer.length === questions.length && showAnswer.every(Boolean);

  if (loading) {
    return (
      <div className="trial-questions-page">
        <div className="loading-message">Preparando suas questões...</div>
      </div>
    );
  }

  return (
    <div className="trial-questions-page">
      <h2 className="trial-questions-title">Sessão de treino gratuita</h2>
      <p className="trial-questions-subtitle">
        Separamos 10 questões dos tópicos mais cobrados na prova para você praticar agora mesmo.
      </p>

      {questions.length > 0 && (
        <SingleFreeQuestionView
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          showAnswer={showAnswer}
          onAnswer={handleAnswer}
          awnsers={AWNSERS}
          getAnswers={getAnswers}
          getQuestionImageSrc={getQuestionImageSrc}
          hasMore={false}
          loadingMore={false}
          onLoadMore={() => {}}
        />
      )}

      {allAnswered && (
        <div className="trial-questions-finish">
          <button type="button" className="trial-questions-finish-button" onClick={goToResults}>
            Ver meu resultado
          </button>
        </div>
      )}
    </div>
  );
}

export default TrialQuestions;
